// Mock data for the Investment Indicators screen (route `/investment-indicators`).
// Follows the same data-first pattern used by the performance and instructions screens.
// This is a partial fixture: indicator cards, quick links, and the Basic Info block
// are fully populated; the Technical block contains the first MCP-verified nested
// group only ("株価変化率"). Remaining blocks are stubbed with `rows: []` so components
// keep compiling — later waves fill them in.

import type {
  InvestmentIndicatorCardData,
  InvestmentIndicatorsPageData,
  InvestmentInfoBlockData,
  InvestmentQuickLink,
} from '@app-types/investmentIndicators';

// ---- Quick links row -----------------------------------------------
const quickLinks = [
  { key: 'ql-1', label: '主要投資指標', nodeId: '3164:13455' },
  { key: 'ql-2', label: '基本情報', nodeId: '3164:13456' },
  { key: 'ql-3', label: 'コンセンサス情報', nodeId: '3164:13457' },
  { key: 'ql-4', label: '株式属性', nodeId: '3164:13458' },
  { key: 'ql-5', label: 'テクニカル', nodeId: '3164:13459' },
  { key: 'ql-6', label: '財務', nodeId: '3164:13460' },
];

const jumpLinks: InvestmentQuickLink[] = [
  { key: 'basic', label: '基本情報 ▼', href: '#investment-basic', nodeId: '3164:13455' },
  {
    key: 'consensus',
    label: 'コンセンサス情報 ▼',
    href: '#investment-consensus',
    nodeId: '3164:13456',
  },
  { key: 'finance', label: '財務（連） ▼', href: '#investment-finance', nodeId: '3164:13457' },
  {
    key: 'stockAttributes',
    label: '銘柄属性 ▼',
    href: '#investment-stock-attributes',
    nodeId: '3164:13458',
  },
  { key: 'technical', label: 'テクニカル ▼', href: '#investment-technical', nodeId: '3164:13459' },
  {
    key: 'technicalIndicators',
    label: 'テクニカル指標 ▼',
    href: '#investment-technical-indicators',
    nodeId: '3164:13460',
  },
];

// ---- Indicator cards (PBR / PER / ROE / ROA) -----------------------
const indicators: InvestmentIndicatorCardData[] = [
  {
    key: 'pbr',
    nodeIdWeb: '3169:8238',
    nodeIdMobile: '3169:8609',
    label: 'PBR',
    valueDisplay: '35.12倍',
    unit: '倍',
    minLabel: '0',
    maxLabel: '50倍',
    minValue: 0,
    maxValue: 50,
    scaleMode: 'zero-start',
    company: { label: '三菱UFJ', value: 18.42, display: '18.42倍' },
    industry: { label: '業種平均', value: 1.9, display: '1.90倍' },
  },
  {
    key: 'per',
    nodeIdWeb: '3169:8259',
    nodeIdMobile: '3336:18997',
    label: 'PER',
    valueDisplay: '18.42倍',
    unit: '倍',
    minLabel: '0',
    maxLabel: '50倍',
    minValue: 0,
    maxValue: 50,
    scaleMode: 'zero-start',
    company: { label: '三菱UFJ', value: 18.42, display: '18.42倍' },
    industry: { label: '業種平均', value: 14.1, display: '14.10倍' },
  },
  {
    key: 'roe',
    nodeIdWeb: '3169:8323',
    nodeIdMobile: '3169:8611',
    label: 'ROE',
    valueDisplay: '9.75%',
    unit: '%',
    minLabel: '-50%',
    maxLabel: '50%',
    minValue: -50,
    maxValue: 50,
    scaleMode: 'centered-zero',
    company: { label: '三菱UFJ', value: 9.75, display: '9.75%' },
    industry: { label: '業種平均', value: 10.5, display: '10.50%' },
  },
  {
    key: 'roa',
    nodeIdWeb: '3169:8344',
    nodeIdMobile: '3336:19022',
    label: 'ROA',
    valueDisplay: '4.20%',
    unit: '%',
    minLabel: '-50%',
    maxLabel: '50%',
    minValue: -50,
    maxValue: 50,
    scaleMode: 'centered-zero',
    company: { label: '三菱UFJ', value: 4.2, display: '4.20%' },
    industry: { label: '業種平均', value: 5.0, display: '5.00%' },
  },
];

// ---- Left column: Basic Information --------------------------------
const basic: InvestmentInfoBlockData = {
  key: 'basic',
  anchorId: 'investment-basic',
  title: '基本情報',
  sectionNodeIdWeb: '3164:13473',
  sectionNodeIdMobile: '3336:19050',
  headerNodeIdWeb: '3164:13474',
  headerNodeIdMobile: '3336:19051',
  tableNodeIdWeb: '3164:13475',
  tableNodeIdMobile: '3336:19052',
  rows: [
    { key: 'market', label: '主市場', value: 'プライム市場', nodeIdWeb: '3164:13476' },
    { key: 'marketCap', label: '時価総額(百万円)', value: '57,019,914\n百万円', nodeIdWeb: '3164:13477' },
    { key: 'tradingUnit', label: '売買単位', value: '100株', nodeIdWeb: '3164:13478' },
    {
      key: 'indexAdopted',
      label: '指数採用銘柄',
      value: 'JPX日経400 / 日経225 / 日経300 / TOPIX / TOPIX100',
      nodeIdWeb: '3164:13479',
    },
    { key: 'toshoSector', label: '東証33業種', value: '', nodeIdWeb: '3164:13480' },
    { key: 'consensusRating', label: 'コンセンサスレーティング', value: '3.8 (やや強気)', nodeIdWeb: '3164:13481' },
  ],
};

// ---- Left column: Consensus Information (MCP-verified) -------------
const consensus: InvestmentInfoBlockData = {
  key: 'consensus',
  anchorId: 'investment-consensus',
  title: 'コンセンサス情報',
  sectionNodeIdWeb: '3224:13535',
  sectionNodeIdMobile: '3336:19059',
  headerNodeIdWeb: '3224:13536',
  headerNodeIdMobile: '3336:19060',
  tableNodeIdWeb: '3224:13537',
  tableNodeIdMobile: '3336:19061',
  rows: [
    { key: 'per-forecast', label: 'PER(株価収益率)(予)(倍)', value: '13.46倍', nodeIdWeb: '3224:13538' },
    // 3-column rows (label + period sub-label + value) are modeled as a group
    // with a single child. Visually identical to a group row.
    {
      key: 'per-change-rate',
      label: 'PER変化率(予)(%)',
      nodeIdWeb: '3224:13539',
      children: [
        { key: 'per-change-1y', label: '1年前', value: '42.82%' },
      ],
    },
    { key: 'eps-forecast', label: 'EPS(予)(一株あたり当期利益)(円)', value: '268.27円', nodeIdWeb: '3224:13540' },
    { key: 'peg-forecast', label: 'PEG(予)(倍)', value: '-0.53倍', nodeIdWeb: '3224:13541' },
    { key: 'bps-forecast', label: 'BPS(予)(一株あたり純資産)(円)', value: '2,921.88円', nodeIdWeb: '3224:13542' },
    { key: 'dividend-yield-forecast', label: '配当利回り(予)(%)', value: '2.67%', nodeIdWeb: '3224:13543' },
    {
      key: 'dividend-yield-change-rate',
      label: '配当利回り変化率(予)',
      nodeIdWeb: '3224:13544',
      children: [
        { key: 'dyc-1y', label: '1年前', value: '-18.75%' },
      ],
    },
    { key: 'revenue-growth-5y-past', label: '過去5年増収比率(予)(%)', value: '15.36%', nodeIdWeb: '3224:13545' },
    { key: 'revenue-growth-3y-avg', label: '過去3年平均売上高成長率(予)(%)', value: '5.88%', nodeIdWeb: '3224:13546' },
    { key: 'earnings-revision', label: '業績予想修正率(予)', value: '0.01%', nodeIdWeb: '3224:13547' },
    {
      key: 'operating-profit-change-rate',
      label: '経常利益変化率(予)',
      groupLabelVerticalAlign: 'top',
      nodeIdWeb: '3224:13548',
      children: [
        { key: 'opcr-5d', label: '5営業日前', value: '0.01%', nodeIdWeb: '3224:13549' },
        { key: 'opcr-20d', label: '20営業日前', value: '1.95%', nodeIdWeb: '3224:13550' },
        { key: 'opcr-3m', label: '3ヶ月前', value: '6.55%', nodeIdWeb: '3224:13551' },
        { key: 'opcr-6m', label: '6ヶ月前', value: '5.60%', nodeIdWeb: '3474:21788' },
        { key: 'opcr-1y', label: '1年前', value: '-19.44%', nodeIdWeb: '3474:21802' },
      ],
    },
    { key: 'debt-to-income', label: '有利子負債/当期利益(予)(%)', value: '10.95%', nodeIdWeb: '3224:13554' },
  ],
};

// ---- Left column: Stock Attributes (MCP-verified) ------------------
// Note: Figma title is 銘柄属性 (not 株式属性 as originally in the plan).
const stockAttributes: InvestmentInfoBlockData = {
  key: 'stockAttributes',
  anchorId: 'investment-stock-attributes',
  title: '銘柄属性',
  sectionNodeIdWeb: '3164:13502',
  sectionNodeIdMobile: '3336:19079',
  headerNodeIdWeb: '3164:13503',
  headerNodeIdMobile: '3336:19080',
  tableNodeIdWeb: '3164:13504',
  tableNodeIdMobile: '3336:19081',
  rows: [
    { key: 'margin-buy', label: '信用残(買)', value: '7,115,200', nodeIdWeb: '3164:13505' },
    { key: 'margin-sell', label: '信用残(売)', value: '3,494,800', nodeIdWeb: '3164:13506' },
    { key: 'wow-buy', label: '前週比(買)', value: '-7,000', nodeIdWeb: '3164:13507' },
    { key: 'wow-sell', label: '前週比(売)', value: '-652,800', nodeIdWeb: '3164:13508' },
    { key: 'margin-ratio', label: '信用倍率(倍)', value: '2.04倍', nodeIdWeb: '3164:13509' },
  ],
};

// ---- Left column: Technical (MCP-verified) -------------------------
const technical: InvestmentInfoBlockData = {
  key: 'technical',
  anchorId: 'investment-technical',
  title: 'テクニカル',
  sectionNodeIdWeb: '3164:13510',
  sectionNodeIdMobile: '3336:19087',
  headerNodeIdWeb: '3164:13511',
  headerNodeIdMobile: '3336:19088',
  tableNodeIdWeb: '3164:13512',
  tableNodeIdMobile: '3336:19089',
  rows: [
    // Top rows (simple 2-column)
    { key: 'stock-price', label: '株価(円)', value: '3,610.0円 (02/02 10:01)', nodeIdWeb: '3164:13513' },
    { key: 'prev-day-diff', label: '前日比', value: '+106\n(+303%)', tone: 'positive', nodeIdWeb: '3164:13514' },
    // Nested group: Stock price change rate (7 periods)
    {
      key: 'stock-price-change-rate',
      label: '株価変化率',
      groupLabelVerticalAlign: 'top',
      nodeIdWeb: '3164:13515',
      children: [
        { key: 'spcr-5d', label: '5営業日前', value: '3.83%', nodeIdWeb: '3167:7798' },
        { key: 'spcr-20d', label: '20営業日前', value: '7.57%', nodeIdWeb: '3164:13517' },
        { key: 'spcr-3m', label: '3ヶ月前', value: '15.04%', nodeIdWeb: '3164:13518' },
        { key: 'spcr-6m', label: '6ヶ月前', value: '32.50%', nodeIdWeb: '3164:13519' },
        { key: 'spcr-1y', label: '1年前', value: '21.41%', nodeIdWeb: '3164:13520' },
        { key: 'spcr-3y', label: '3年前', value: '89.95%', nodeIdWeb: '3164:13521' },
        { key: 'spcr-5y', label: '5年前', value: '147.26%', nodeIdWeb: '3164:13522' },
      ],
    },
    // Year high/low rows (simple 2-column)
    { key: 'ytd-low', label: '年初来安値(円)', value: '2,226.0円', nodeIdWeb: '3164:13523' },
    { key: 'ytd-low-rise', label: '年初来安値からの上昇率(%)', value: '62.14%', nodeIdWeb: '3164:13524' },
    { key: 'ytd-high', label: '年初来高値(円)', value: '3,764.0円', nodeIdWeb: '3164:13525' },
    { key: 'ytd-high-drop', label: '年初来高値からの下落率(%)', value: '-4.09%', nodeIdWeb: '3164:13526' },
    // Nested group: Average trading volume (2 periods)
    {
      key: 'avg-trading-value',
      label: '平均売買代金(千円)',
      groupLabelVerticalAlign: 'top',
      nodeIdWeb: '3164:13527',
      children: [
        { key: 'atv-5d', label: '5日', value: '66,248,769\n千円', nodeIdWeb: '3164:13528' },
        { key: 'atv-20d', label: '20日', value: '80,003,915\n千円', nodeIdWeb: '3164:13529' },
      ],
    },
    // Nested group: Volume increase rate (5 periods)
    {
      key: 'volume-increase-rate',
      label: '出来高増加率(倍)',
      groupLabelVerticalAlign: 'top',
      nodeIdWeb: '3164:13530',
      children: [
        { key: 'vir-5d', label: '5営業日前', value: '0.23', nodeIdWeb: '3164:13531' },
        { key: 'vir-20d', label: '20営業日前', value: '0.54', nodeIdWeb: '3164:13532' },
        { key: 'vir-3m', label: '3ヶ月前', value: '0.25', nodeIdWeb: '3164:13533' },
        { key: 'vir-6m', label: '6ヶ月前', value: '0.23', nodeIdWeb: '3164:13534' },
        { key: 'vir-1y', label: '1年前', value: '0.31', nodeIdWeb: '3164:13535' },
      ],
    },
    // Nested group: Increase rate of trading volume (5 periods)
    {
      key: 'trading-value-increase-rate',
      label: '売買代金増加率(倍)',
      groupLabelVerticalAlign: 'top',
      nodeIdWeb: '3164:13536',
      children: [
        { key: 'tvir-5d', label: '5営業日前', value: '0.24', nodeIdWeb: '3164:13537' },
        { key: 'tvir-20d', label: '20営業日前', value: '0.58', nodeIdWeb: '3164:13538' },
        { key: 'tvir-3m', label: '3ヶ月前', value: '0.29', nodeIdWeb: '3164:13539' },
        { key: 'tvir-6m', label: '6ヶ月前', value: '0.31', nodeIdWeb: '3164:13540' },
        { key: 'tvir-1y', label: '1年前', value: '0.38', nodeIdWeb: '3164:13541' },
      ],
    },
    // Nested group: Percentage deviation from moving average (3 periods)
    {
      key: 'ma-deviation',
      label: '株価移動平均線からの乖離率(%)',
      groupLabelVerticalAlign: 'top',
      nodeIdWeb: '3164:13542',
      children: [
        { key: 'mad-5d', label: '5営業日前', value: '3.93%', nodeIdWeb: '3164:13543' },
        { key: 'mad-25d', label: '25営業日前', value: '3.63%', nodeIdWeb: '3164:13544' },
        { key: 'mad-75d', label: '75営業日前', value: '11.18%', nodeIdWeb: '3164:13545' },
      ],
    },
    // Trailing rows
    {
      key: 'rsi',
      label: 'RSI',
      nodeIdWeb: '3164:13546',
      children: [{ key: 'rsi-14d', label: '過去14日', value: '48.29' }],
    },
    { key: 'psychological-line', label: 'サイコロジカルライン', value: '50.00', nodeIdWeb: '3164:13547' },
    { key: 'normal-stochastics', label: 'ノーマルストキャスティクス', value: '63.59', nodeIdWeb: '3164:13548' },
    { key: 'slow-stochastic', label: 'スローストキャスティクス', value: '43.60', nodeIdWeb: '3164:13549' },
    { key: 'beta-topix', label: 'ベータ(対TOPIX)', value: '1.27', nodeIdWeb: '3164:13550' },
    { key: 'beta-nikkei', label: 'ベータ(対日経平均)', value: '1.04', nodeIdWeb: '3164:13551' },
    { key: 'beta-tse-sector', label: 'ベータ(対東証業種指数)', value: '1.12', nodeIdWeb: '3164:13552' },
  ],
};

// ---- Right column: Finance (MCP-verified) --------------------------
// Figma title is 財務（連）(consolidated), not just 財務 as originally in the plan.
const finance: InvestmentInfoBlockData = {
  key: 'finance',
  anchorId: 'investment-finance',
  title: '財務（連）',
  sectionNodeIdWeb: '3164:13554',
  sectionNodeIdMobile: '3474:22569',
  headerNodeIdWeb: '3164:13555',
  headerNodeIdMobile: '3474:22570',
  tableNodeIdWeb: '3164:13556',
  tableNodeIdMobile: '3474:22571',
  rows: [
    { key: 'per', label: 'PER(株価収益率)(倍)', value: '10.04倍', nodeIdWeb: '3164:13557' },
    {
      key: 'per-change-rate',
      label: 'PER変化率(%)',
      groupLabelVerticalAlign: 'top',
      nodeIdWeb: '3164:13558',
      children: [
        { key: 'per-cr-1y', label: '1年前', value: '-3.11%', nodeIdWeb: '3164:13559' },
        { key: 'per-cr-3y', label: '3年前', value: '-4.15%', nodeIdWeb: '3164:13560' },
        { key: 'per-cr-5y', label: '5年前', value: '-6.40%', nodeIdWeb: '3164:13561' },
      ],
    },
    { key: 'eps', label: 'EPS(一株あたり当期利益)(円)', value: '359.56円', nodeIdWeb: '3164:13562' },
    { key: 'pbr', label: 'PBR(株価純資産倍率)(倍)', value: '1.31倍', nodeIdWeb: '3164:13563' },
    {
      key: 'pbr-change-rate',
      label: 'PBR変化率(%)',
      groupLabelVerticalAlign: 'top',
      nodeIdWeb: '3164:13564',
      children: [
        { key: 'pbr-cr-1y', label: '1年前', value: '-12.18%', nodeIdWeb: '3164:13565' },
        { key: 'pbr-cr-3y', label: '3年前', value: '45.71%', nodeIdWeb: '3164:13566' },
        { key: 'pbr-cr-5y', label: '5年前', value: '27.40%', nodeIdWeb: '3164:13567' },
      ],
    },
    { key: 'div-yield', label: '配当利回り(%)', value: '2.49%', nodeIdWeb: '3164:13568' },
    {
      key: 'div-yield-change-rate',
      label: '配当利回り変化率(%)',
      groupLabelVerticalAlign: 'top',
      nodeIdWeb: '3164:13569',
      children: [
        { key: 'dycr-1y', label: '1年前', value: '-27.53%', nodeIdWeb: '3164:13570' },
        { key: 'dycr-3y', label: '3年前', value: '-21.88%', nodeIdWeb: '3164:13571' },
        { key: 'dycr-5y', label: '5年前', value: '-17.38%', nodeIdWeb: '3164:13572' },
      ],
    },
    { key: 'roe', label: 'ROE(自己資本利益率)(%)', value: '13.60%', nodeIdWeb: '3164:13573' },
    { key: 'roa', label: 'ROA(総資産当期利益率)(%)', value: '--%', nodeIdWeb: '3164:13574' },
    { key: 'revenue', label: '売上高(百万円)', value: '48,036,704\n百万円', nodeIdWeb: '3164:13575' },
    {
      key: 'revenue-change-rate',
      label: '売上高変化率(%)',
      groupLabelVerticalAlign: 'top',
      nodeIdWeb: '3164:13576',
      children: [{ key: 'rcr-yoy', label: '前年度比', value: '6.52%' }],
    },
    { key: 'psr', label: 'PSR(株価売上高倍率)(倍)', value: '0.94倍', nodeIdWeb: '3164:13577' },
    { key: 'gross-margin', label: '海外売上高比率(%)', value: '54.50%', nodeIdWeb: '3164:13578' },
    { key: 'operating-margin', label: '売上高営業利益率(%)', value: '9.98%', nodeIdWeb: '3164:13579' },
    { key: 'ordinary-margin', label: '売上高経常利益率(%)', value: '13.35%', nodeIdWeb: '3164:13580' },
    { key: 'net-turnover', label: '売上債権回転率(回)', value: '3.48回', nodeIdWeb: '3164:13581' },
    { key: 'ordinary-profit', label: '経常利益(経営に係る利益)\n(百万円)', value: '6,414,590\n百万円', nodeIdWeb: '3164:13582' },
    {
      key: 'ordinary-profit-change-rate',
      label: '経常利益変化率(%)',
      nodeIdWeb: '3164:13583',
      children: [{ key: 'opcr-yoy', label: '前年度比', value: '-7.90%' }],
    },
    { key: 'equity-ratio', label: '自己資本比率(%)', value: '38.38%', nodeIdWeb: '3164:13584' },
    { key: 'prev-total-assets', label: '前期最終損益(百万円)', value: '4,765,085\n百万円', nodeIdWeb: '3164:13585' },
    { key: 'debt-equity-ratio', label: '有利子負債自己資本比率(%)', value: '106.50%', nodeIdWeb: '3164:13586' },
    { key: 'ev-ebitda', label: 'EV/EBITDA倍率(倍)', value: '12.02倍', nodeIdWeb: '3164:13587' },
    { key: 'current-ratio', label: '流動比率(%)', value: '125.97%', nodeIdWeb: '3164:13588' },
    { key: 'quick-ratio', label: '当座比率(%)', value: '110.35%', nodeIdWeb: '3164:13589' },
    { key: 'pcfr', label: 'PCFR(株価キャッシュフロー倍率)', value: '7.58倍', nodeIdWeb: '3164:13590' },
    { key: 'inventory-turnover', label: '棚卸資産回転率(回)', value: '8.36回', nodeIdWeb: '3164:13591' },
    { key: 'margin-volume-ratio', label: '信用残/売買高レシオ(倍)', value: '0.17倍', nodeIdWeb: '3164:13592' },
    { key: 'interest-coverage', label: 'インタレストカバレッジレシオ(倍)', value: '--倍', nodeIdWeb: '3164:13593' },
  ],
};

// ---- Right column: Technical Indicators (MCP-verified) -------------
const technicalIndicators: InvestmentInfoBlockData = {
  key: 'technicalIndicators',
  anchorId: 'investment-technical-indicators',
  title: 'テクニカル指標',
  sectionNodeIdWeb: '3164:13594',
  sectionNodeIdMobile: '3474:22326',
  headerNodeIdWeb: '3164:13595',
  headerNodeIdMobile: '3474:22327',
  tableNodeIdWeb: '3164:13596',
  tableNodeIdMobile: '3474:22328',
  rows: [
    // Cross rows use three cells: indicator name | API tradeDate | API signal.
    // Values stay '--' here; dynamic date/signal come solely from API mapping.
    {
      key: 'mid-golden-cross',
      label: '中期ゴールデンクロス',
      nodeIdWeb: '3164:13597',
      children: [{ key: 'mid-golden-cross-signal', label: '--', value: '--' }],
    },
    {
      key: 'short-golden-cross',
      label: '短期ゴールデンクロス',
      nodeIdWeb: '3164:13598',
      children: [{ key: 'short-golden-cross-signal', label: '--', value: '--' }],
    },
    {
      key: 'mid-dead-cross',
      label: '中期デッドクロス',
      children: [{ key: 'mid-dead-cross-signal', label: '--', value: '--' }],
    },
    {
      key: 'short-dead-cross',
      label: '短期デッドクロス',
      children: [{ key: 'short-dead-cross-signal', label: '--', value: '--' }],
    },
    // Simple 2-column rows
    { key: 'ichimoku-up', label: '一目均衡雲抜け上', value: '○', nodeIdWeb: '3164:13599' },
    { key: 'ichimoku-down', label: '一目均衡雲抜け下', value: 'X', nodeIdWeb: '3164:13600' },
    // Groups with 2 children each (buy signal / sell signal)
    {
      key: 'dmi-daily',
      label: 'DMI - 日足',
      groupLabelVerticalAlign: 'top',
      nodeIdWeb: '3164:13601',
      children: [
        { key: 'dmi-d-buy', label: '買いシグナル', value: 'X', nodeIdWeb: '3164:13602' },
        { key: 'dmi-d-sell', label: '売りシグナル', value: 'X', nodeIdWeb: '3164:13603' },
      ],
    },
    {
      key: 'dmi-weekly',
      label: 'DMI - 週足',
      groupLabelVerticalAlign: 'top',
      nodeIdWeb: '3164:13604',
      children: [
        { key: 'dmi-w-buy', label: '買いシグナル', value: 'X', nodeIdWeb: '3164:13605' },
        { key: 'dmi-w-sell', label: '売りシグナル', value: 'X', nodeIdWeb: '3164:13606' },
      ],
    },
    {
      key: 'rci',
      label: 'RCI',
      groupLabelVerticalAlign: 'top',
      nodeIdWeb: '3164:13607',
      children: [
        { key: 'rci-buy', label: '買いシグナル', value: 'X', nodeIdWeb: '3164:13608' },
        { key: 'rci-sell', label: '売りシグナル', value: 'X', nodeIdWeb: '3164:13609' },
      ],
    },
    {
      key: 'macd-daily',
      label: 'MACD - 日足',
      groupLabelVerticalAlign: 'top',
      nodeIdWeb: '3164:13610',
      children: [
        { key: 'macd-d-buy', label: '買いシグナル', value: 'X', nodeIdWeb: '3164:13611' },
        { key: 'macd-d-sell', label: '売りシグナル', value: 'X', nodeIdWeb: '3164:13612' },
      ],
    },
    {
      key: 'macd-weekly',
      label: 'MACD - 週足',
      groupLabelVerticalAlign: 'top',
      nodeIdWeb: '3164:13613',
      children: [
        { key: 'macd-w-buy', label: '買いシグナル', value: 'X', nodeIdWeb: '3164:13614' },
        { key: 'macd-w-sell', label: '売りシグナル', value: 'X', nodeIdWeb: '3164:13615' },
      ],
    },
    {
      key: 'normal-stochastics',
      label: 'ノーマルストキャスティクス',
      groupLabelVerticalAlign: 'top',
      nodeIdWeb: '3164:13616',
      children: [
        { key: 'ns-buy', label: '買いシグナル', value: 'X', nodeIdWeb: '3164:13617' },
        { key: 'ns-sell', label: '売りシグナル', value: 'X', nodeIdWeb: '3164:13618' },
      ],
    },
    {
      key: 'slow-stochastic',
      label: 'スローストキャスティクス',
      groupLabelVerticalAlign: 'top',
      nodeIdWeb: '3164:13619',
      children: [
        { key: 'ss-buy', label: '買いシグナル', value: 'X', nodeIdWeb: '3164:13620' },
        { key: 'ss-sell', label: '売りシグナル', value: 'X', nodeIdWeb: '3164:13621' },
      ],
    },
  ],
};

// ---- Assembled page data -------------------------------------------
export const mockInvestmentIndicators: InvestmentIndicatorsPageData = {
  quickLinks: jumpLinks,
  indicators,
  informationColumns: [
    { key: 'left', blocks: [basic, consensus, stockAttributes, technical] },
    { key: 'right', blocks: [finance, technicalIndicators] },
  ],
};

export default mockInvestmentIndicators;
